import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, FileText, IndianRupee, MapPin, Phone, UserRound } from "lucide-react";
import client, { unwrap } from "@/api/client";
import { cn } from "@/lib/utils";
import { Field, IconInput, inputShell, selectShell } from "@/components/forms/FormField";
import { PROPERTY_TYPES, SOURCES, STAGES } from "@/components/enquiries/enquiryMeta";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const NONE = "none";
const emptyForm = (status) => ({
  customerId: "",
  name: "",
  phone: "",
  propertyType: "",
  city: "",
  source: "",
  showroomId: NONE,
  assigneeId: NONE,
  status,
  potentialValue: "",
  notes: "",
});

function validate(form, isNewCustomer) {
  const errors = {};
  if (isNewCustomer && !form.name.trim()) errors.name = "Enter the customer's name";
  if (!isNewCustomer && !form.customerId) errors.customerId = "Choose a customer";
  if (form.potentialValue && !(Number(form.potentialValue) >= 0)) errors.potentialValue = "Enter a valid amount";
  return errors;
}

function PlainSelect({ value, onChange, placeholder, testId, error, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className={selectShell(error)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// `initialStatus` presets the stage (per-column add buttons); `initialMode` picks
// existing vs. new customer (the Add Enquiry split-button menu).
export default function AddEnquiryDialog({ open, setOpen, initialStatus = "NEW", initialMode = "existing" }) {
  const queryClient = useQueryClient();
  const [isNewCustomer, setIsNewCustomer] = useState(initialMode === "new");
  const [form, setForm] = useState(() => emptyForm(initialStatus));
  const [errors, setErrors] = useState({});
  // If the customer is created but the enquiry fails, a retry reuses that customer.
  const createdCustomer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm(initialStatus));
    setIsNewCustomer(initialMode === "new");
    setErrors({});
    createdCustomer.current = null;
  }, [open, initialStatus, initialMode]);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => client.get("/customers").then(unwrap),
    enabled: open,
  });
  const { data: options = { showrooms: [], members: [] } } = useQuery({
    queryKey: ["enquiry-options"],
    queryFn: () => client.get("/enquiries/options").then(unwrap),
    enabled: open,
  });

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const onInput = (key) => (e) => set(key)(e.target.value);

  const pickShowroom = (showroomId) => {
    const showroom = options.showrooms.find((s) => s.id === showroomId);
    setForm((f) => ({ ...f, showroomId, city: f.city || showroom?.city || "" }));
  };

  const createEnquiry = useMutation({
    mutationFn: async () => {
      let customerId = form.customerId;
      if (isNewCustomer) {
        const key = `${form.name.trim()}|${form.phone.trim()}`;
        if (createdCustomer.current?.key !== key) {
          const res = await client.post("/customers", { name: form.name.trim(), phone: form.phone.trim() || undefined });
          createdCustomer.current = { key, id: unwrap(res).id || unwrap(res)._id };
        }
        customerId = createdCustomer.current.id;
      }
      return client.post("/enquiries", {
        customerId,
        status: form.status,
        source: form.source || undefined,
        propertyType: form.propertyType.trim() || undefined,
        city: form.city.trim() || undefined,
        showroomId: form.showroomId === NONE ? undefined : form.showroomId,
        assigneeId: form.assigneeId === NONE ? undefined : form.assigneeId,
        potentialValue: form.potentialValue ? Number(form.potentialValue) : undefined,
        notes: form.notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success("Enquiry created");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["enquiries"] });
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-attention-badge"] });
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to create enquiry"),
  });

  const submit = (e) => {
    e.preventDefault();
    const found = validate(form, isNewCustomer);
    setErrors(found);
    if (Object.keys(found).length === 0) createEnquiry.mutate();
  };

  const customerTab = (isNew, label, testId) => (
    <button
      type="button"
      role="tab"
      aria-selected={isNewCustomer === isNew}
      data-testid={testId}
      onClick={() => {
        setIsNewCustomer(isNew);
        setErrors({});
      }}
      className={cn(
        "flex-1 rounded-md px-3 py-1.5 text-sm transition-colors",
        isNewCustomer === isNew ? "bg-card text-foreground shadow-sm font-medium" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-enquiry-dialog" className="max-w-[calc(100vw-2rem)] sm:max-w-2xl p-0 gap-0 rounded-2xl sm:rounded-2xl overflow-hidden">
        <form onSubmit={submit} noValidate className="max-h-[90vh] overflow-y-auto px-6 sm:px-8 py-7">
          <DialogTitle className="font-serif text-2xl sm:text-[1.75rem] font-normal text-foreground pr-6">Add Enquiry</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-muted-foreground">
            Capture a new lead and where it came from. You can move it along the pipeline later.
          </DialogDescription>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <div className="sm:col-span-2">
              <div role="tablist" aria-label="Customer" className="flex rounded-lg bg-secondary p-1 mb-3">
                {customerTab(false, "Existing customer", "new-enquiry-existing-customer-toggle")}
                {customerTab(true, "New customer", "new-enquiry-new-customer-toggle")}
              </div>
              {isNewCustomer ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
                  <Field label="Customer Name" required error={errors.name} htmlFor="enq-name">
                    <IconInput id="enq-name" icon={UserRound} data-testid="new-enquiry-customer-name-input" placeholder="e.g. Neha Sharma" value={form.name} onChange={onInput("name")} error={errors.name} />
                  </Field>
                  <Field label="Phone Number" htmlFor="enq-phone">
                    <IconInput id="enq-phone" icon={Phone} type="tel" data-testid="new-enquiry-customer-phone-input" placeholder="e.g. +91 98765 43210" value={form.phone} onChange={onInput("phone")} />
                  </Field>
                </div>
              ) : (
                <Field label="Customer" required error={errors.customerId}>
                  <Select value={form.customerId} onValueChange={set("customerId")}>
                    <SelectTrigger data-testid="new-enquiry-customer-select" className={selectShell(errors.customerId)}>
                      <SelectValue placeholder={customers.length ? "Select customer" : "No customers yet — add a new one"} />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c._id} value={c._id}>
                          {c.name}
                          {c.phone ? ` · ${c.phone}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>

            <Field label="Property" optional htmlFor="enq-property">
              <IconInput
                id="enq-property"
                icon={Building2}
                list="enq-property-types"
                data-testid="new-enquiry-property-input"
                placeholder="e.g. 3 BHK Apartment"
                value={form.propertyType}
                onChange={onInput("propertyType")}
              />
              <datalist id="enq-property-types">
                {PROPERTY_TYPES.map((p) => <option key={p} value={p} />)}
              </datalist>
            </Field>
            <Field label="City" optional htmlFor="enq-city">
              <IconInput id="enq-city" icon={MapPin} data-testid="new-enquiry-city-input" placeholder="e.g. Mumbai" value={form.city} onChange={onInput("city")} />
            </Field>

            <Field label="Source" optional>
              <PlainSelect
                value={form.source}
                onChange={set("source")}
                placeholder="How did they find you?"
                testId="new-enquiry-source-select"
                options={SOURCES.map((s) => ({ value: s.value, label: s.value }))}
              />
            </Field>
            <Field label="Showroom" optional>
              <PlainSelect
                value={form.showroomId}
                onChange={pickShowroom}
                testId="new-enquiry-showroom-select"
                options={[{ value: NONE, label: "No showroom" }, ...options.showrooms.map((s) => ({ value: s.id, label: s.name }))]}
              />
            </Field>

            <Field label="Stage">
              <PlainSelect
                value={form.status}
                onChange={set("status")}
                testId="new-enquiry-stage-select"
                options={STAGES.filter((s) => s.key !== "LOST").map((s) => ({ value: s.key, label: s.label }))}
              />
            </Field>
            <Field label="Assign To" optional>
              <PlainSelect
                value={form.assigneeId}
                onChange={set("assigneeId")}
                testId="new-enquiry-assignee-select"
                options={[{ value: NONE, label: "Me" }, ...options.members.map((m) => ({ value: m.id, label: m.name }))]}
              />
            </Field>

            <Field label="Potential Value" optional error={errors.potentialValue} htmlFor="enq-value" className="sm:col-span-2">
              <IconInput
                id="enq-value"
                icon={IndianRupee}
                type="number"
                min="0"
                inputMode="numeric"
                data-testid="new-enquiry-potential-value-input"
                placeholder="e.g. 250000"
                value={form.potentialValue}
                onChange={onInput("potentialValue")}
                error={errors.potentialValue}
              />
            </Field>

            <Field label="Notes" optional htmlFor="enq-notes" className="sm:col-span-2">
              <div className={cn(inputShell, "h-auto items-start py-3 border-border")}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.75} />
                <textarea
                  id="enq-notes"
                  data-testid="new-enquiry-notes-input"
                  rows={3}
                  placeholder="Rooms, budget, timeline, preferences…"
                  value={form.notes}
                  onChange={onInput("notes")}
                  className="flex-1 min-w-0 resize-y bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
            </Field>
          </div>

          <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button type="button" variant="outline" className="sm:min-w-[8.5rem] border-accent/40 text-accent hover:bg-accent/5 hover:text-accent" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="new-enquiry-submit-button"
              disabled={createEnquiry.isPending}
              className="sm:min-w-[8.5rem] bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {createEnquiry.isPending ? "Creating…" : "Create Enquiry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
